import React, { useState, useEffect } from "react";
import "../styles/ManageMember.css";
import "../styles/ManageAdvertisingAgent.css";
import SideBar from "../components/SideBar";
import { useLocation, useNavigate } from "react-router-dom";
import { businessAgentAPI } from "../services/businessAgentService";
import {
  formatToDDMMYYYY,
  convertHTMLDateToDDMMYYYY,
  convertDDMMYYYYToHTMLDate,
} from "../utils/dateUtils";

function ManageBusinessAgent() {
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [RequestDate, setRequestDate] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [totalAgents, setTotalAgents] = useState(0);
  const [filteredAgentsCount, setFilteredAgentsCount] = useState(0);
  const [currentPageAgentsCount, setCurrentPageAgentsCount] = useState(0);
  const itemsPerPage = 10;
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch PENDING applications only for Request History
  const fetchPendingApplications = async () => {
    setLoading(true);
    setError("");
    try {
      const filters = {
        page: currentPage,
        per_page: itemsPerPage,
        status: "pending", // Only pending applications
        agent_type: type !== "all" ? type : undefined,
        search: search || undefined,
      };

      const response = await businessAgentAPI.getAgentApplications(filters);
      setApplications(response.applications || []);
      
      // Use pagination total as fallback if getBusinessAgentCount fails
      if (response.pagination && response.pagination.total && totalAgents === 0) {
        setTotalAgents(response.pagination.total);
      }
    } catch (err) {
      setError("Failed to fetch pending applications");
      console.error("Error fetching pending applications:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch total business agent count with filters
  const fetchTotalAgentCount = async () => {
    try {
      // Prepare filters for the count API
      const filters = {};
      
      if (search) {
        filters.search = search;
      }
      
      if (type !== 'all') {
        filters.agent_type = type;
      }
      
      // Note: We're counting pending applications only for this page
      filters.status = "pending";
      
      const response = await businessAgentAPI.getBusinessAgentCount(filters);
      
      // Check different possible response structures
      if (response.count !== undefined) {
        setTotalAgents(response.count);
      } else if (response.total !== undefined) {
        setTotalAgents(response.total);
      } else if (response.business_count !== undefined) {
        setTotalAgents(response.business_count);
      } else {
        // If the response structure is different, try to extract count from the first property
        const count = Object.values(response)[0];
        if (typeof count === 'number') {
          setTotalAgents(count);
        } else {
          console.warn('Unexpected API response format:', response);
          // Fallback: fetch count from pagination in fetchPendingApplications
        }
      }
    } catch (error) {
      console.error('Error fetching business agent count:', error);
      // Don't show error to user for count failure, use fallback
    }
  };

  useEffect(() => {
    fetchTotalAgentCount();
  }, [search, type]);

  useEffect(() => {
    fetchPendingApplications();
  }, [currentPage, type, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, type, RequestDate]);

  // Calculate filtered agents count whenever applications change
  useEffect(() => {
    if (applications.length > 0) {
      // Filter applications based on frontend filters
      const filtered = applications.filter((application) => {
        // Extract name from customer_details
        const customerName = application.customer_details 
          ? `${application.customer_details.first_name || ''} ${application.customer_details.last_name || ''}`.trim()
          : application.customer_name || '';
        
        const searchMatch =
          customerName.toLowerCase().includes(search.toLowerCase()) ||
          (application.customer_details?.email || '')
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          (application.email || '')
            .toLowerCase()
            .includes(search.toLowerCase());

        const typeMatch =
          type === "all" ||
          application.agent_type?.toLowerCase() === type.toLowerCase();

        // Date filtering - compare in dd/mm/yyyy format
        const applicationDate = formatToDDMMYYYY(application.applied_at || application.created_at);
        const dateMatch = RequestDate === "all" || applicationDate === RequestDate;

        return searchMatch && dateMatch && typeMatch;
      });

      // Calculate paginated agents
      const startIndex = (currentPage - 1) * itemsPerPage;
      const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

      // Update statistics
      setFilteredAgentsCount(filtered.length);
      setCurrentPageAgentsCount(paginated.length);
    } else {
      setFilteredAgentsCount(0);
      setCurrentPageAgentsCount(0);
    }
  }, [applications, currentPage, search, type, RequestDate]);

  const goToRequestHistory = () => {
    navigate("/manage-business-agent", { state: { applications } });
  };

  const goToApprovedHistory = () => {
    navigate("/business-agent-approved-history", {
      state: { applications },
    });
  };

  const goToRejectedHistory = () => {
    navigate("/business-agent-rejected-history", {
      state: { applications },
    });
  };

  // Handle date filter change
  const handleDateChange = (e) => {
    const htmlDate = e.target.value;
    const ddmmyyyyDate = convertHTMLDateToDDMMYYYY(htmlDate);
    setRequestDate(ddmmyyyyDate);
  };

  // Get current HTML date value for the input
  const getHTMLDateValue = () => {
    return RequestDate === "all" ? "" : convertDDMMYYYYToHTMLDate(RequestDate);
  };

  // Filter applications based on frontend filters
  const filteredApplications = applications.filter((application) => {
    // Extract name from customer_details
    const customerName = application.customer_details 
      ? `${application.customer_details.first_name || ''} ${application.customer_details.last_name || ''}`.trim()
      : application.customer_name || '';
    
    const searchMatch =
      customerName.toLowerCase().includes(search.toLowerCase()) ||
      (application.customer_details?.email || '')
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      (application.email || '')
        .toLowerCase()
        .includes(search.toLowerCase());

    const typeMatch =
      type === "all" ||
      application.agent_type?.toLowerCase() === type.toLowerCase();

    // Date filtering - compare in dd/mm/yyyy format
    const applicationDate = formatToDDMMYYYY(application.applied_at || application.created_at);
    const dateMatch = RequestDate === "all" || applicationDate === RequestDate;

    return searchMatch && dateMatch && typeMatch;
  });

  const totalPages = Math.ceil((totalAgents || 0) / itemsPerPage);
  const paginatedApplications = filteredApplications; // Already paginated from API

  const handleRowClick = (application) => {
    console.log("Navigating to application details:", application);

    // Prepare agent data for details page
    const agentData = {
      ...application.customer_details, // Spread customer details
      application_id: application.application_id || application.id, // Add application_id to agent object
      agent_type: application.agent_type,
      status: application.status,
      applied_at: application.applied_at || application.created_at,
      reviewed_at: application.reviewed_at,
      rejection_reason: application.rejection_reason,
      photo_url: application.photo_url || application.customer_details?.photo_url || application.customer_details?.profile_picture || application.customer_details?.profile_image || application.customer_details?.profile_photo || application.customer_details?.avatar || application.profile_image || application.profile_photo || application.avatar || null,
      is_already_agent: application.is_already_agent,
      // Include additional fields for business agent
      business_name: application.business_name || application.customer_details?.business_name,
      experience_years: application.experience_years || application.customer_details?.experience_years,
      first_name: application.first_name || application.customer_details?.first_name,
      last_name: application.last_name || application.customer_details?.last_name,
      email: application.email || application.customer_details?.email,
      phone_number: application.phone_number || application.customer_details?.phone_number,
    };

    navigate("/business-agent-details", {
      state: {
        agent: agentData,
        application: application, // Pass full application data
        isPendingApplication: true,
      },
    });
  };

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{border:0, boxShadow:"none"}}>
          <div className="header-top">
            <h2 className="header-title">Manage Business Agent</h2>
          </div>

          {/* Business Agent Statistics Section - Added */}
          <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total Pending Applications:</span>
              <span className="stat-value">{totalAgents}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Filtered Applications:</span>
              <span className="stat-value">{filteredAgentsCount}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Current Page:</span>
              <span className="stat-value">
                {paginatedApplications.length}
              </span>
            </div>
          </div>

          <div className="filters">
            <div className="input-group">
              <label>Filter by Type:</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="mini">Mini Business Agent</option>
                <option value="senior">Senior Business Agent</option>
              </select>
            </div>

            <div className="input-group">
              <label>Filter by Request Date:</label>
              <input
                type="date"
                value={getHTMLDateValue()}
                onChange={handleDateChange}
                className="date-filter-select"
               maxLength={255} />
            </div>

            <div className="input-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
               maxLength={20} />
            </div>
          </div>

          <div className="agent-top-bar">
            <div className="top-buttons">
              <button className="tab-btn active" onClick={goToRequestHistory}>
                Request History
              </button>
              <button className="tab-btn" onClick={goToApprovedHistory}>
                Approved History
              </button>
              <button className="tab-btn" onClick={goToRejectedHistory}>
                Rejected History
              </button>
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Type</th>
                <th>Request Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center" }}>
                    <div className="loading-message">Loading Applications...</div>
                  </td>
                </tr>
              ) : paginatedApplications.length > 0 ? (
                paginatedApplications.map((application, i) => {
                  // Get the customer name from application data
                  const customerName = application.customer_details 
                    ? `${application.customer_details.first_name || ''} ${application.customer_details.last_name || ''}`.trim()
                    : application.first_name 
                      ? `${application.first_name} ${application.last_name || ''}`.trim()
                      : 'N/A';
                  
                  const customerEmail = application.customer_details?.email || application.email || 'N/A';
                  const agentType = application.agent_type === "senior"
                    ? "Senior Business Agent"
                    : "Mini Business Agent";
                  const requestDate = formatToDDMMYYYY(application.applied_at || application.created_at);
                  
                  return (
                    <tr
                      key={application.application_id || application.id || i}
                      onClick={() => handleRowClick(application)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{customerName}</td>
                      <td>{customerEmail}</td>
                      <td>{agentType}</td>
                      <td>{requestDate}</td>
                      <td>
                        <span
                          className={`status ${application.status?.toLowerCase()}`}
                        >
                          {application.status || "Pending"}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center" }}>
                    {filteredAgentsCount === 0 && totalAgents > 0 
                      ? "No pending applications match your filters" 
                      : "No pending applications found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination - Only show when there are filtered applications */}
          {!loading && filteredAgentsCount > 0 && (
            <div className="pagination">
              <button
                className="page-btn"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button
                className="page-btn"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ManageBusinessAgent;